import React from 'react';
import { track } from '../../lib/analytics';

export const ConstellationProfileCard = ({
  node,
  onClose,
  onViewProfile,
  onOpenChat,
  actionLoading = false,
}) => {
  if (!node) return null;

  const resolvedUserId = node.user_id || node.userId || node.user?.id || node.id || null;
  const resolvedMatchId = node.match_id || node.matchId || node.conversation_id || null;

  // Stop taps from bubbling into the canvas/backdrop
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent?.stopImmediatePropagation?.();
  };

  const handleButton = (e, fn) => {
    stop(e);
    fn();
  };

  const handleViewProfileClick = () => {
    if (!resolvedUserId) return;
    track("profile_opened_from_universe", { target_user_id: resolvedUserId });
    onViewProfile(resolvedUserId);
  };

  const handleChatClick = () => {
    if (resolvedMatchId) {
      track("chat_opened_from_universe", { target_user_id: resolvedUserId, match_id: resolvedMatchId });
      onOpenChat({ matchId: resolvedMatchId, userId: resolvedUserId });
      return;
    }
    if (resolvedUserId) {
      track("chat_opened_from_universe", { target_user_id: resolvedUserId });
      onOpenChat({ userId: resolvedUserId });
    }
  };

  const getConnectionLabel = () => {
    if (node.connection_type === 'bonded') return '💜 Bonded connection';
    if (node.connection_type === 'strong') return '⭐ Strong connection';
    return '🌑 Light connection';
  };

  const getConnectionColor = () => {
    if (node.connection_type === 'bonded') return 'text-purple-300';
    if (node.connection_type === 'strong') return 'text-yellow-300';
    return 'text-gray-400';
  };

  return (
    <>
      {/* Backdrop — use pointerdown for mobile reliability */}
      <div
        className="fixed inset-0 bg-black/40 z-[2147483640] animate-fade-in backdrop-blur-sm"
        onPointerDown={(e) => handleButton(e, onClose)}
        style={{ pointerEvents: 'auto' }}
        data-testid="profile-card-backdrop"
      />

      {/* Card */}
      <div
        className="fixed inset-x-0 bottom-0 z-[2147483647] animate-slide-up"
        style={{ pointerEvents: 'auto' }}
        data-testid="profile-card"
      >
        <div
          className="mx-3 mb-3 rounded-2xl bg-gradient-to-b from-gray-900 to-black border-2 border-purple-700/40 p-5 shadow-2xl"
          onPointerDown={stop} // stops taps from escaping to canvas/backdrop
          style={{ pointerEvents: 'auto' }}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {node.avatar_url ? (
                <img
                  src={node.avatar_url}
                  alt={node.display_name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/50"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-sm border-2 border-purple-500/50">
                  {(node.display_name || '??').slice(0, 2).toUpperCase()}
                </div>
              )}

              <div>
                <div className="text-base font-semibold text-white">
                  {node.display_name}
                  {node.age && <span className="text-gray-400 ml-1.5">· {node.age}</span>}
                </div>
                <div className={`text-xs font-medium mt-0.5 ${getConnectionColor()}`}>
                  {getConnectionLabel()}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  Strength: {node.connection_strength}/100
                </div>
              </div>
            </div>

            {/* Close — pointerdown */}
            <button
              type="button"
              onPointerDown={(e) => handleButton(e, onClose)}
              className="text-gray-400 hover:text-white transition p-1"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={actionLoading || !resolvedUserId}
              onPointerDown={(e) => handleButton(e, handleViewProfileClick)}
              className="flex-1 py-2.5 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition active:scale-95 disabled:opacity-50"
              data-testid="view-profile-btn"
            >
              View Profile
            </button>

            {resolvedMatchId ? (
              <button
                type="button"
                disabled={actionLoading}
                onPointerDown={(e) => handleButton(e, handleChatClick)}
                className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-medium transition active:scale-95 disabled:opacity-50"
                data-testid="open-chat-btn"
              >
                {actionLoading ? 'Opening...' : '💬 Chat'}
              </button>
            ) : (
              <button
                type="button"
                disabled
                className="flex-1 py-2.5 rounded-full bg-gray-700 text-gray-400 text-sm font-medium cursor-not-allowed"
                title="Chat unlocks after a mutual match"
                data-testid="locked-chat-btn"
              >
                🔒 Match to Chat
              </button>
            )}
          </div>

          {!node.match_id && (
            <p className="text-xs text-gray-500 text-center mt-2">
              Chat unlocks after a mutual match
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-fade-in { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </>
  );
};

export default ConstellationProfileCard;
